import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserRole } from '../types';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Container,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Upload as UploadIcon,
  History as HistoryIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
} from '@mui/icons-material';
import { useThemeStore } from '../store/useThemeStore';
import { useAuthStore } from '../store/useAuthStore';
import { useMsal } from '../hooks/useMsal';
import { ROUTES } from '../utils/constants';
import { getCurrentUser } from '../services/adminService';

const DRAWER_WIDTH = 240;

interface NavigationItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
}

export const MainLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { mode, toggleTheme } = useThemeStore();
  const { user, setUser } = useAuthStore();
  const { logout, userInfo } = useMsal();

  // Sync MSAL user info with the auth store, then enrich with the
  // canonical record from the backend so id / authorityId / role
  // reflect the DB rather than whatever stale value persisted in
  // localStorage.
  //
  // Critically, this effect MUST NOT depend on `user`. The .then below
  // calls setUser(...), which writes a new user-object reference into
  // the Zustand store. If `user` were in the deps, every successful
  // /users/me response would trigger a re-run, which would call
  // /users/me again, which would call setUser again — an infinite
  // network loop that also remounts every component subscribed to the
  // auth store on each iteration (the Admin authority dropdown's
  // popover dies on every remount, so it appears to close the moment
  // you click it). We read `user` via the store's snapshot getter for
  // the guard so the value is fresh without being a dep.
  useEffect(() => {
    if (!userInfo) return;
    let cancelled = false;
    const baseUser = {
      id: userInfo.id,
      email: userInfo.email,
      name: userInfo.name,
      role: (userInfo.role as UserRole) || UserRole.ProcurementOfficer,
      createdAt: new Date(),
    };
    const current = useAuthStore.getState().user;
    if (!current || current.id !== userInfo.id) setUser(baseUser);

    getCurrentUser()
      .then((me) => {
        if (cancelled) return;
        setUser({
          ...baseUser,
          id: String(me.id),
          name: me.display_name || baseUser.name,
          authorityId: me.authority_id ?? undefined,
        });
      })
      .catch((err) => {
        console.warn('Failed to fetch /users/me:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [userInfo, setUser]);

  const navigationItems: NavigationItem[] = [
    {
      label: t('navigation.dashboard'),
      path: ROUTES.DASHBOARD,
      icon: <DashboardIcon />,
    },
    {
      label: t('navigation.upload'),
      path: ROUTES.UPLOAD,
      icon: <UploadIcon />,
    },
    {
      label: t('navigation.history'),
      path: ROUTES.HISTORY,
      icon: <HistoryIcon />,
    },
    {
      label: t('navigation.admin'),
      path: ROUTES.ADMIN,
      icon: <AdminIcon />,
      adminOnly: true,
    },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleUserMenuClose();
    navigate(ROUTES.PROFILE);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
    navigate(ROUTES.LOGIN);
  };

  const drawer = (
    <Box>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700 }}>
          VZMR Checker
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {navigationItems.map((item) => {
          const isActive = location.pathname === item.path;

          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                selected={isActive}
                onClick={() => handleNavigate(item.path)}
                sx={{
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    '& .MuiListItemIcon-root': {
                      color: theme.palette.primary.contrastText,
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? 'inherit' : theme.palette.text.secondary,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    // Outer column flex: header band (AppBar is fixed, so it overlays), then
    // the body row (drawer + main content), then the footer as a full-width
    // strip beneath everything. Putting the footer here — outside the drawer
    // + main flex row — lets it span the entire viewport width regardless of
    // any page-internal Container maxWidth.
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="Otevřít boční panel"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {t('pages.dashboard.description')}
          </Typography>

          {/* Theme Toggle */}
          <Tooltip title={t('theme.toggle')}>
            <IconButton color="inherit" onClick={toggleTheme} sx={{ mr: 1 }}>
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          {/* User Menu */}
          <Tooltip title={user?.name || t('navigation.profile')}>
            <IconButton onClick={handleUserMenuOpen} sx={{ p: 0 }}>
              <Avatar
                alt={user?.name || 'Uživatel'}
                sx={{
                  bgcolor: theme.palette.secondary.main,
                  width: 40,
                  height: 40,
                }}
              >
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleProfile}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('navigation.profile')}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('navigation.logout')}</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Body row: drawer + main content. Wrapped in a horizontal flex so the
          footer below us can occupy the full viewport width. */}
      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        {/* Drawer */}
        <Box
          component="nav"
          sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
        >
          {/* Mobile drawer */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better mobile performance
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: DRAWER_WIDTH,
              },
            }}
          >
            {drawer}
          </Drawer>

          {/* Desktop drawer */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: DRAWER_WIDTH,
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>

        {/* Main content */}
        <Box
          component="main"
          id="main-content"
          role="main"
          aria-label="Hlavní obsah"
          sx={{
            flexGrow: 1,
            width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Toolbar /> {/* Spacer for AppBar */}

          <Container
            maxWidth="xl"
            sx={{
              flexGrow: 1,
              py: 3,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Outlet />
          </Container>
        </Box>
      </Box>
      {/* Footer — full viewport width. Stacked above the permanent Drawer
          (theme.zIndex.drawer = 1200) so the drawer visually ends at the
          footer's top edge instead of cutting under it. position:relative is
          required for z-index to take effect. */}
      <Box
        component="footer"
        sx={{
          py: 2,
          px: 3,
          position: 'relative',
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="body2" color="text.secondary" align="center">
          © 2026 MMR ČR - VZMR Checker v1.0.0
        </Typography>
      </Box>
    </Box>
  );
};
